import { isOpenInApp } from "@repo/shared-utils/pwa-utils";

declare const window: {
  clevertap: any;
} & Window;

type PendingClevertapEvent = {
  event: string;
  eventData: Record<string, string>;
  attempts: number;
};

const CT_RETRY_INTERVAL_MS = 500;
const CT_MAX_RETRY_ATTEMPTS = 20;
const CT_MAX_PENDING_EVENTS = 25;

let pendingClevertapEvents: PendingClevertapEvent[] = [];
let retryTimer: ReturnType<typeof setTimeout> | null = null;

const canPushClevertapEvent = (): boolean => {
  return Boolean(window?.clevertap?.event && typeof window.clevertap.event.push === "function");
};

const flushQueuedEvents = (): void => {
  if (!canPushClevertapEvent()) {
    return;
  }

  pendingClevertapEvents.forEach(({ event, eventData }) => {
    window.clevertap.event.push(event, eventData);
  });
  pendingClevertapEvents = [];
};

const scheduleRetry = (): void => {
  if (retryTimer !== null) {
    return;
  }

  retryTimer = setTimeout(() => {
    retryTimer = null;

    if (canPushClevertapEvent()) {
      flushQueuedEvents();
      return;
    }

    pendingClevertapEvents = pendingClevertapEvents
      .map((queuedEvent) => ({ ...queuedEvent, attempts: queuedEvent.attempts + 1 }))
      .filter((queuedEvent) => queuedEvent.attempts <= CT_MAX_RETRY_ATTEMPTS);

    if (pendingClevertapEvents.length > 0) {
      scheduleRetry();
    }
  }, CT_RETRY_INTERVAL_MS);
};

export const clevertapEvent = (
  event: string,
  eventData: Record<string, string>,
): void => {
  const filteredEventData = Object.fromEntries(
    Object.entries(eventData).filter(([, value]) => value !== "NA"),
  );

  if (isOpenInApp()) {
    window.prompt(
      `triggerCTEvent://${JSON.stringify({ eventName: event, props: filteredEventData })}`,
    );
    return;
  }

  if (canPushClevertapEvent()) {
    flushQueuedEvents();
    window.clevertap.event.push(event, filteredEventData);
    return;
  }

  if (pendingClevertapEvents.length >= CT_MAX_PENDING_EVENTS) {
    pendingClevertapEvents.shift();
  }

  pendingClevertapEvents.push({ event, eventData: filteredEventData, attempts: 0 });
  scheduleRetry();
};
